/**
 * Lightweight error monitoring.
 *
 * Right now a crash in production is invisible — the ErrorBoundary shows the
 * user a message but nothing is reported back to us. This module centralises
 * error capture so we have one place to send errors.
 *
 * It is dependency-free and a no-op until configured, so it is safe to ship
 * today. To turn on real reporting, set `VITE_SENTRY_DSN` (or another
 * endpoint) and replace the body of `report()` with your provider's SDK call.
 * Wiring (global handlers, ErrorBoundary, captureError) already routes through
 * here, so enabling a provider is a one-function change.
 */

const IS_DEV = import.meta.env?.DEV ?? false;

let initialized = false;
let reportsSent = 0;
const MAX_REPORTS_PER_SESSION = 10; // guard against error loops flooding the table

/**
 * Deliver an error to our own Supabase (client_errors table) — no third-party
 * service. Lazy-imports the client so monitoring never affects startup, and
 * only reports for signed-in users (RLS requires a user_id).
 */
function report(error, context) {
  if (reportsSent >= MAX_REPORTS_PER_SESSION) return;
  reportsSent++;
  (async () => {
    const { supabase } = await import("./supabase.js");
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;
    await supabase.from("client_errors").insert({
      user_id: session.user.id,
      message: (error?.message || String(error)).slice(0, 1000),
      stack: (error?.stack || "").slice(0, 5000) || null,
      context: context || {},
      url: typeof location !== "undefined" ? location.href : null,
    });
  })().catch(() => { /* never let monitoring throw */ });
}

/**
 * Capture a handled error with optional context.
 * Safe to call from anywhere; never throws.
 */
export function captureError(error, context) {
  if (IS_DEV) console.error("[monitoring]", error, context || "");
  report(error, context);
}

/**
 * Install global handlers for uncaught errors and unhandled promise rejections.
 * Call once at app startup.
 */
export function initMonitoring() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (e) => {
    captureError(e.error || new Error(e.message), { type: "window.error" });
  });
  window.addEventListener("unhandledrejection", (e) => {
    captureError(e.reason || new Error("Unhandled promise rejection"), {
      type: "unhandledrejection",
    });
  });
}
